import dayjs from 'dayjs'
import { TimeRange } from 'features/systemLens'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useInterval } from 'react-use'
import { ChartTimeUnit, DateMode } from 'types'

const DEFAULT_CADENCE_SECONDS = 10

interface UseDateModeProps {
  setTimeRange: Dispatch<SetStateAction<TimeRange>>
  timeRange: TimeRange
  initialCadenceSeconds?: number
  relativeStart?: { value?: number; unit: ChartTimeUnit }
  relativeEnd?: { isNow: boolean; value?: number; unit: ChartTimeUnit }
}

export const useDateMode = ({
  setTimeRange,
  timeRange,
  initialCadenceSeconds,
  relativeStart,
  relativeEnd,
}: UseDateModeProps) => {
  const [dateMode, internalSetDateMode] = useState<DateMode>('relative')
  const [liveTail, setLiveTail] = useState(false)
  const [cadenceSeconds, setCadenceSeconds] = useState<number>(
    initialCadenceSeconds ?? DEFAULT_CADENCE_SECONDS
  )

  const lastAbsoluteTimeRangeRef = useRef<TimeRange | null>(null)
  const lastRelativeTimeRangeRef = useRef<TimeRange | null>(null)

  useEffect(() => {
    if (dateMode === 'absolute') {
      lastAbsoluteTimeRangeRef.current = timeRange
    } else if (dateMode === 'relative') {
      lastRelativeTimeRangeRef.current = timeRange
    }
  }, [timeRange, dateMode])

  const setDateMode: Dispatch<SetStateAction<DateMode>> = useCallback(
    (updater) => {
      internalSetDateMode((prev) => {
        const newDateMode =
          typeof updater === 'function' ? updater(prev) : updater
        setLiveTail(newDateMode === 'relative')

        if (newDateMode === 'absolute' && lastAbsoluteTimeRangeRef.current) {
          setTimeRange(lastAbsoluteTimeRangeRef.current)
        } else if (
          newDateMode === 'relative' &&
          lastRelativeTimeRangeRef.current
        ) {
          setTimeRange(lastRelativeTimeRangeRef.current)
        }

        return newDateMode
      })
    },
    [setTimeRange]
  )

  useEffect(() => {
    if (dateMode === 'relative' && liveTail && relativeStart?.value) {
      const now = dayjs()
      const from = now.subtract(relativeStart.value, relativeStart.unit)
      const to = relativeEnd?.isNow
        ? now
        : now.subtract(relativeEnd?.value || 0, relativeEnd?.unit || 'days')

      setTimeRange((prev) => ({
        ...prev,
        from: from.toISOString(),
        to: to.toISOString(),
      }))
    }
  }, [
    dateMode,
    liveTail,
    relativeStart?.value,
    relativeStart?.unit,
    relativeEnd?.isNow,
    relativeEnd?.value,
    relativeEnd?.unit,
    setTimeRange,
  ])

  useInterval(() => {
    if (!liveTail || !relativeStart?.value) {
      return
    }
    const now = dayjs()
    const from = now.subtract(relativeStart.value, relativeStart.unit)
    const to = relativeEnd?.isNow
      ? now
      : now.subtract(relativeEnd?.value || 0, relativeEnd?.unit || 'days')

    setTimeRange((prev) => ({
      ...prev,
      from: from.toISOString(),
      to: to.toISOString(),
    }))
  }, cadenceSeconds * 1000)

  return {
    dateMode,
    setDateMode,
    liveTail,
    setLiveTail,
    cadenceSeconds,
    setCadenceSeconds,
  }
}
